import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import type { PeerComparisonResponse } from '../../api/rankingApi';
import RankingDetailModal from './RankingDetailModal';
import RankingHighlightedText from './RankingHighlightedText';
import { resolveRingDisplay, type RingKey } from '../../utils/rankingDisplay';
import './RankingRingChart.css';
import './SwotMiniChart.css';

export const RANKING_CHART_HEIGHT = 220;

export interface RankingRingChartProps {
  data?: PeerComparisonResponse | null;
  /** Diameter of the dial in px. */
  size?: number;
}

interface RingDef {
  key: RingKey;
  label: string;
  color: string;
  trackColor: string;
}

const RINGS: RingDef[] = [
  { key: 'consistency', label: 'Consistency', color: '#f31e5a', trackColor: '#2a0812' },
  { key: 'technical', label: 'Interview', color: '#8B5CF6', trackColor: '#140d24' },
  { key: 'coding', label: 'Coding', color: '#8cf402', trackColor: '#1a2a00' },
  { key: 'gd', label: 'GD', color: '#00dad9', trackColor: '#001819' },
];

interface RingTooltipProps {
  label: string;
  color: string;
  value: string | null;
}

const RingTooltipContent: React.FC<RingTooltipProps> = ({ label, color, value }) => (
  <>
    <div
      className="swot-mini-tooltip-badge"
      style={{ backgroundColor: `${color}33`, color }}
    >
      {label}
    </div>
    <div className="swot-mini-tooltip-hierarchy">
      <div className="swot-mini-tooltip-level subdimension">
        <div className="swot-mini-tooltip-header">
          <span className="swot-mini-tooltip-icon">▸</span>
          <span className="swot-mini-tooltip-label">Performance score</span>
        </div>
        <div className="swot-mini-tooltip-content">
          <span className="swot-mini-tooltip-name">{label}</span>
          <span className="swot-mini-tooltip-score" style={{ color: value ? color : '#6b7280' }}>
            {value ?? '—'}
          </span>
        </div>
      </div>
    </div>
  </>
);

const RankingRingChart: React.FC<RankingRingChartProps> = ({ data, size = 152 }) => {
  const isPending = !data || data.rankings_pending === true;
  const canOpenModal = !isPending && !!data;
  const [hoveredRing, setHoveredRing] = useState<RingKey | null>(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [modalOpen, setModalOpen] = useState(false);
  const [focusRing, setFocusRing] = useState<RingKey | null>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePos({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const ringValues = useMemo(() => {
    const map = {} as Record<RingKey, ReturnType<typeof resolveRingDisplay>>;
    for (const ring of RINGS) {
      map[ring.key] = isPending
        ? {
            fillPercent: null,
            compactText: '—',
            hoverLabel: ring.label,
            hoverValue: null,
            showPeerComparison: false,
          }
        : resolveRingDisplay(ring.key, data);
    }
    return map;
  }, [data, isPending]);

  const center = size / 2;
  const strokeWidth = Math.max(11, Math.round(size * 0.088));
  const gap = Math.max(0, Math.round(strokeWidth * 0.1));
  const outerRadius = center - strokeWidth / 2 - 2;

  const openModal = (ring?: RingKey) => {
    if (!canOpenModal) return;
    setFocusRing(ring ?? null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setFocusRing(null);
  };

  const handleWidgetClick = () => {
    openModal(hoveredRing ?? undefined);
  };

  const handleWidgetKeyDown = (e: React.KeyboardEvent) => {
    if (!canOpenModal) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openModal(hoveredRing ?? undefined);
    }
  };

  const hoveredDef = hoveredRing ? RINGS.find((r) => r.key === hoveredRing) : null;
  const hoveredValue = hoveredRing ? ringValues[hoveredRing] : null;

  return (
    <>
      <div
        className={`ranking-ring-chart ${canOpenModal ? 'ranking-ring-chart--clickable' : ''}`}
        style={{ height: RANKING_CHART_HEIGHT }}
        onClick={canOpenModal ? handleWidgetClick : undefined}
        onKeyDown={handleWidgetKeyDown}
        role={canOpenModal ? 'button' : undefined}
        tabIndex={canOpenModal ? 0 : undefined}
        aria-label={canOpenModal ? 'View peer performance details' : undefined}
      >
        <div className="ranking-ring-dial" style={{ width: size, height: size }}>
          <svg
            width={size}
            height={size}
            viewBox={`0 0 ${size} ${size}`}
            role="img"
            aria-label="Peer ranking dial"
          >
            {RINGS.map((ring, index) => {
              const radius = outerRadius - index * (strokeWidth + gap);
              const value = ringValues[ring.key];
              const showArc = value.fillPercent != null && value.fillPercent > 0;
              const isCompleteRing = value.fillPercent != null && value.fillPercent >= 99;

              return (
                <g
                  key={ring.key}
                  className="ranking-ring-group"
                  onMouseEnter={() => setHoveredRing(ring.key)}
                  onMouseLeave={() => setHoveredRing(null)}
                >
                  <circle
                    cx={center}
                    cy={center}
                    r={radius}
                    fill="none"
                    stroke={ring.trackColor}
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    className="ranking-ring-track"
                  />
                  {showArc && (
                    <circle
                      cx={center}
                      cy={center}
                      r={radius}
                      fill="none"
                      stroke={ring.color}
                      strokeWidth={strokeWidth}
                      strokeLinecap="round"
                      {...(isCompleteRing
                        ? {}
                        : {
                            pathLength: 100,
                            strokeDasharray: `${value.fillPercent} 100`,
                            transform: `rotate(-90 ${center} ${center})`,
                          })}
                      className="ranking-ring-arc"
                    />
                  )}
                  <circle
                    cx={center}
                    cy={center}
                    r={radius}
                    fill="none"
                    stroke="transparent"
                    strokeWidth={strokeWidth + 10}
                    pointerEvents="stroke"
                    className="ranking-ring-hit"
                  />
                </g>
              );
            })}
          </svg>

          {isPending && (
            <div className="ranking-ring-center-note">
              <span className="ranking-ring-center-title">Calculating…</span>
            </div>
          )}
        </div>

        <div className="ranking-ring-side">
          {isPending ? (
            <p className="ranking-ring-pending">Your standings are being calculated…</p>
          ) : (
            <ul className="ranking-ring-messages">
              {RINGS.map((ring) => {
                const value = ringValues[ring.key];

                return (
                  <li key={ring.key} className="ranking-ring-message-row">
                    <span className="ranking-ring-dot" style={{ backgroundColor: ring.color }} aria-hidden />
                    <p className="ranking-ring-message-text">
                      <RankingHighlightedText text={value.compactText} />
                    </p>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {hoveredDef && hoveredValue &&
          createPortal(
            <div
              className="swot-mini-tooltip-portal"
              style={{
                position: 'fixed',
                left: mousePos.x + 15,
                top: mousePos.y - 10,
                zIndex: 999999,
                pointerEvents: 'none',
              }}
            >
              <div className="swot-mini-tooltip">
                <RingTooltipContent
                  label={hoveredValue.hoverLabel}
                  color={hoveredDef.color}
                  value={hoveredValue.hoverValue}
                />
              </div>
            </div>,
            document.body
          )}
      </div>

      {modalOpen && data &&
        createPortal(
          <RankingDetailModal data={data} focusRing={focusRing} onClose={closeModal} />,
          document.body
        )}
    </>
  );
};

export default RankingRingChart;
